

/**
 * FloatingEdge - Edge that dynamically calculates its connection points
 * Instead of connecting to fixed handles, the edge endpoints float to the
 * optimal position on the node boundary.
 *
 * @module react-flow/edges/FloatingEdge
 */

import { Refresh as RefreshCw, Delete as Trash2 } from '@mui/icons-material';
import { EdgeLabelRenderer, type EdgeProps, getBezierPath, useInternalNode } from '@xyflow/react';
import { useState } from 'react';

import { getEdgeParams, type NodeLike } from './edge-utils';

interface FloatingEdgeData {
  onInvert?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export function FloatingEdge({
  id,
  source,
  target,
  markerEnd,
  style,
  data,
}: EdgeProps) {
  const [isHovered, setIsHovered] = useState(false);
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);

  if (!sourceNode || !targetNode) {
    return null;
  }

  const {
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  } = getEdgeParams(sourceNode as unknown as NodeLike, targetNode as unknown as NodeLike);

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <path
        id={id}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
        style={{
          strokeWidth: isHovered ? 4 : 2,
          stroke: isHovered ? '#6366f1' : '#b1b1b7',
          transition: 'stroke-width 0.2s, stroke 0.2s',
          ...style,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />
      {/* Invisible wider path for better hover interaction */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ cursor: 'pointer' }}
      />

      {isHovered && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
              zIndex: 1000,
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div
              style={{
                display: 'flex',
                gap: '4px',
                padding: '4px',
                background: 'white',
                borderRadius: '20px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                border: '1px solid #e2e8f0',
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  (data as unknown as FloatingEdgeData)?.onInvert?.(id);
                }}
                title="Invert connection"
                style={{
                  padding: '4px',
                  borderRadius: '100%',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#64748b',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <RefreshCw sx={{ fontSize: 14 }} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  (data as unknown as FloatingEdgeData)?.onDelete?.(id);
                }}
                title="Delete connection"
                style={{
                  padding: '4px',
                  borderRadius: '100%',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ef4444',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#fee2e2')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <Trash2 sx={{ fontSize: 14 }} />
              </button>
            </div>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

export default FloatingEdge;
